import React, { useState, useEffect, useRef } from "react";
import Wordmark from "../components/Wordmark.jsx";
import EditClientModal from "../components/coordinator/EditClientModal.jsx";
import NewClientModal from "../components/coordinator/NewClientModal.jsx";
import AccessPanel from "../components/AccessPanel.jsx";
import StaffModal from "../components/StaffModal.jsx";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth.jsx";
import UserChip from "../components/UserChip.jsx";
import ClientNameBlock from "../components/ClientNameBlock.jsx";
import { useIsCompact } from "../hooks/useIsCompact.js";

function Topbar({ agencyName, user, onLogout, compact = false }) {
  // Phone: the agency name is already the page heading below; drop it here
  // so the bar is the wordmark and the user chip, nothing to push past the edge.
  return (
    <div style={{ ...tb.bar, ...(compact ? tb.barCompact : {}) }}>
      <div style={{ ...tb.left, minWidth: 0 }}>
        <Wordmark />
        {agencyName && !compact && <span style={tb.agencyName}>{agencyName}</span>}
      </div>
      <div style={tb.right}>
        <UserChip user={user} onLogout={onLogout} />
      </div>
    </div>
  );
}

function StorageBar({ used, limit, pct }) {
  const fmt = b => {
    if (!b) return "0 MB";
    if (b < 1048576) return `${(b/1024).toFixed(0)} KB`;
    if (b < 1073741824) return `${(b/1048576).toFixed(1)} MB`;
    return `${(b/1073741824).toFixed(2)} GB`;
  };
  const fill  = Math.min(pct ?? 0, 100);
  const color = fill > 85 ? "var(--color-critical)" : fill > 65 ? "var(--color-status-draft)" : "var(--color-accent)";
  return (
    <div style={{ display:"flex",flexDirection:"column",gap:6,width:"100%" }}>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"baseline" }}>
        <span style={{ fontSize:"var(--text-xs)",fontWeight:"var(--weight-semibold)",color:"var(--color-text-tertiary)",textTransform:"uppercase",letterSpacing:"0.06em" }}>Storage</span>
        <span style={{ fontSize:"var(--text-xs)",color:"var(--color-text-secondary)" }}>{fmt(used)}{limit ? ` / ${fmt(limit)}` : " used"}</span>
      </div>
      <div style={{ height:6,background:"var(--color-bg-inset)",borderRadius:3,overflow:"hidden" }}>
        <div style={{ height:"100%",borderRadius:3,width:`${fill}%`,background:color }} />
      </div>
    </div>
  );
}

function PersonRow({ person, onRemove }) {
  const [busy, setBusy] = useState(false);

  async function remove() {
    if (!window.confirm(
      `Remove ${person.display_name || person.full_name} from the team? ` +
      `They'll lose access to everything. Anything they've added stays.`
    )) return;
    setBusy(true);
    try { await onRemove(person); } finally { setBusy(false); }
  }

  return (
    <div style={pr.row}>
      <div style={pr.avatar}>{person.initials}</div>
      <div style={{ flex:1 }}>
        <p style={pr.name}>
          {person.full_name}
          {/* Invited but never signed in. With eighty DSPs, "did they get the
              link?" is a real question and the roster is where it's asked. */}
          {person.pending && <span style={pr.pendingTag}>hasn't signed in</span>}
        </p>
        {person.email && <p style={pr.email}>{person.email}</p>}
      </div>
      {/* Contributors carry a relationship rather than a caseload: what matters
          about Ada is that she's Maris's mother, not that she's on one client.
          Coordinators carry neither — their access doesn't come from
          assignments, so a count of them would mean nothing. */}
      {person.relationship
        ? <span style={pr.relationship}>{person.relationship}</span>
        : person.client_count > 0 && <span style={pr.count}>{person.client_count} client{person.client_count!==1?"s":""}</span>}
      {onRemove && (
        <button style={pr.removeBtn} onClick={remove} disabled={busy}>
          {busy ? "…" : "Remove"}
        </button>
      )}
    </div>
  );
}

/**
 * A person who has left.
 *
 * Deliberately quieter than PersonRow — no avatar, no caseload count, no
 * Remove. This list is history, and at roughly 45% annual DSP turnover it is
 * the part of the roster that grows fastest. It should read as a record you
 * consult, not a roster you manage.
 */
function FormerRow({ person, onReinvite }) {
  const left = person.disabled_at
    ? new Date(person.disabled_at).toLocaleDateString(undefined,
        { month: "short", day: "numeric", year: "numeric" })
    : null;

  return (
    <div style={fr.row}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={fr.name}>{person.display_name}</p>
        <p style={fr.meta}>
          {person.was}{left && ` · left ${left}`}
        </p>
      </div>
      {/* Offered for former STAFF only. A parent or friend was invited from a
          client's page because they know that client; bringing them back
          through the hiring modal would make them agency staff. The API sends
          a null was_role for exactly those people, so the row still appears —
          an administrator should see everyone who lost access — but without an
          action that would do the wrong thing. */}
      {person.was_role && (
        <button style={fr.reinviteBtn} onClick={() => onReinvite(person)}>
          Re-invite
        </button>
      )}
    </div>
  );
}

const fr = {
  row:         { display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderBottom: "1px solid var(--color-border)" },
  name:        { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", color: "var(--color-text-secondary)", margin: 0 },
  meta:        { fontSize: 11, color: "var(--color-text-tertiary)", margin: "2px 0 0" },
  reinviteBtn: { font: "inherit", fontSize: "var(--text-xs)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "5px 12px", color: "var(--color-text-secondary)", cursor: "pointer", flexShrink: 0 },
};

// The branding panel saves a logo (as a data URL) and an accent colour to
// agency.brand_settings — and nothing reads them back. No page shows the logo,
// no stylesheet uses the colour. A settings panel whose settings do nothing is
// worse than none: an admin uploads a logo, sees no change, and concludes the
// product is broken. Hidden until there is somewhere for them to appear; the
// component and the endpoint stay so switching it on is one flag.
const SHOW_BRANDING = false;

// ── Branding panel ─────────────────────────────────────────────────────────────
// Displays agency logo + accent color. Both are editable.
// Logo stored as brand_settings.logo_data_url (base64 data URL).
// Accent color stored as brand_settings.accent_color.
// Full theming-from-brand-settings (re-skinning the running app) is deferred —
// the data round-trips correctly; applying it to CSS variables waits for multi-agency.

function BrandingPanel({ agency, onUpdated }) {
  const logoInputRef   = useRef();
  const [color, setColor] = useState(agency?.brand_settings?.accent_color || "#5A4FCF");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const logoUrl = agency?.brand_settings?.logo_data_url;

  async function uploadLogo(file) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async e => {
      const dataUrl = e.target.result;
      setSaving(true);
      await fetch("/api/agency", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ brand_settings: { logo_data_url: dataUrl } }),
      });
      setSaving(false);
      onUpdated();
    };
    reader.readAsDataURL(file);
  }

  async function saveColor() {
    setSaving(true);
    await fetch("/api/agency", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ brand_settings: { accent_color: color } }),
    });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    onUpdated();
  }

  return (
    <div style={brand.wrap}>
      <h2 style={styles.sectionHeading}>Branding</h2>

      {/* Logo */}
      <div style={brand.logoRow}>
        {logoUrl
          ? <img src={logoUrl} alt="Agency logo" style={brand.logoImg} />
          : <div style={brand.logoPlaceholder}>No logo</div>
        }
        <div style={{ display:"flex",flexDirection:"column",gap:6 }}>
          <button style={brand.uploadBtn} onClick={() => logoInputRef.current.click()} disabled={saving}>
            {saving ? "Uploading…" : "Upload logo"}
          </button>
          <p style={brand.uploadHint}>SVG, PNG, or JPG</p>
        </div>
        <input ref={logoInputRef} type="file" accept="image/svg+xml,image/png,image/jpeg"
          style={{ display:"none" }} onChange={e => uploadLogo(e.target.files[0])} />
      </div>

      {/* Accent color */}
      <div style={brand.colorRow}>
        <div>
          <p style={brand.colorLabel}>Accent color</p>
          <p style={brand.colorHint}>Displayed on the agency dashboard. App-wide theming is deferred.</p>
        </div>
        <div style={{ display:"flex",alignItems:"center",gap:10 }}>
          <input type="color" value={color} onChange={e => setColor(e.target.value)}
            style={brand.colorInput} />
          <span style={{ fontSize:"var(--text-xs)",color:"var(--color-text-tertiary)",fontFamily:"monospace" }}>{color}</span>
          <button style={brand.saveColorBtn} onClick={saveColor} disabled={saving}>
            {saved ? "Saved ✓" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Access settings (Oct 19) ──────────────────────────────────────────────────
// Agency-wide choices, each defaulting to the original behaviour:
//   who can see a NEW client (each client can still be changed on its own page)
//   who can add clients
//   how DSPs' notes reach the next shift (Getting Good, Nov 2026; off by default)
function AccessSettingsPanel({ settings, onSaved }) {
  const [form, setForm]   = useState(settings);
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => { setForm(settings); }, [settings]);

  const dirty = form.new_client_coordinator_access !== settings.new_client_coordinator_access
             || form.client_creation !== settings.client_creation
             || (form.shift_sharing ?? "off") !== (settings.shift_sharing ?? "off");

  async function save() {
    setBusy(true); setError(null); setSaved(false);
    try {
      const r = await fetch("/api/agency", {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ settings: form }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Couldn't save that.");
      setSaved(true);
      onSaved?.();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  const field = (key, label, options) => (
    <div style={acc.field}>
      <p style={acc.label}>{label}</p>
      {options.map(([v, title, sub]) => (
        <label key={v} style={acc.option}>
          <input type="radio" name={key} value={v} checked={form[key] === v} disabled={busy}
                 onChange={() => { setForm(f => ({ ...f, [key]: v })); setSaved(false); }} />
          <span><span style={acc.optTitle}>{title}</span><span style={acc.optSub}>{sub}</span></span>
        </label>
      ))}
    </div>
  );

  return (
    <div>
      <h2 style={styles.sectionHeading}>Access settings</h2>
      <div style={acc.box}>
        {field("new_client_coordinator_access", "Which coordinators can see a new client", [
          ["all", "All coordinators", "Every coordinator in the agency."],
          ["selected", "Only chosen coordinators", "Starts with just the person who added it. You choose the rest on each client's page."],
        ])}
        {field("client_creation", "Who can add clients", [
          ["coordinators", "You and coordinators", ""],
          ["admins", "Only you", "Coordinators won't see a New client button."],
        ])}
        <p style={acc.hint}>Changing these doesn't alter existing clients.</p>
        {"shift_sharing" in settings && field("shift_sharing", "Shift notes between DSPs", [
          ["off", "Off", "Notes go to the coordinator only."],
          ["reviewed", "Reviewed", "A coordinator shares notes with the next shift."],
          ["immediate", "Immediate", "Notes can reach the next shift at once; flagged notes wait for a coordinator. Shift notes last 14 days unless added to the profile."],
        ])}
        {error && <p style={acc.error}>{error}</p>}
        <div style={acc.actions}>
          <button type="button" style={{ ...acc.save, ...(!dirty || busy ? acc.disabled : {}) }}
                  disabled={!dirty || busy} onClick={save}>{busy ? "Saving…" : "Save"}</button>
          {saved && !dirty && <span style={acc.saved}>Saved</span>}
        </div>
      </div>
    </div>
  );
}

const acc = {
  box:      { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: 16, display: "flex", flexDirection: "column", gap: 14 },
  field:    { display: "flex", flexDirection: "column", gap: 6 },
  label:    { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", color: "var(--color-text-primary)", margin: 0 },
  option:   { display: "flex", gap: 8, alignItems: "flex-start", fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", cursor: "pointer" },
  optTitle: { display: "block" },
  optSub:   { display: "block", fontSize: 11, color: "var(--color-text-tertiary)", lineHeight: 1.4 },
  hint:     { fontSize: 11, color: "var(--color-text-tertiary)", margin: 0 },
  error:    { fontSize: "var(--text-sm)", color: "var(--color-critical)", margin: 0 },
  actions:  { display: "flex", alignItems: "center", gap: 12 },
  save:     { font: "inherit", fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", background: "var(--color-accent)", color: "var(--color-text-on-accent)", border: "none", borderRadius: "var(--radius-md)", padding: "6px 18px", cursor: "pointer" },
  disabled: { opacity: 0.5, cursor: "not-allowed" },
  saved:    { fontSize: "var(--text-xs)", color: "var(--color-accent)" },
};

// ── Main ───────────────────────────────────────────────────────────────────────

export default function AgencyDashboard() {
  const { user, logout } = useAuth();
  const navigate          = useNavigate();
  const compact           = useIsCompact();
  const [data, setData]   = useState(null);
  const [error, setError] = useState(null);
  const [showStaff, setShowStaff] = useState(false);
  const [showNewClient, setShowNewClient] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [accessTarget, setAccessTarget] = useState(null);   // C8: admin invites too
  // Collapsed by default — see the section below for why that is a decision
  // rather than a default.
  const [showFormer, setShowFormer] = useState(false);
  // Non-null while re-inviting someone: the prefill handed to StaffModal.
  const [reinvite, setReinvite] = useState(null);

  // Re-invite reuses the working, tested invitation path instead of adding an
  // endpoint. invitations.py already recognises a disabled user as a rehire and
  // reactivates the SAME row on acceptance — same user.id, so everything she
  // ever added stays attributed to her and no duplicate-email account appears.
  function startReinvite(person) {
    setReinvite({
      email:       person.email,
      name:        person.display_name,
      role:        person.was_role || "caregiver",
      lastClients: person.last_clients || [],
    });
  }

  // Removing someone is deactivation, not deletion: they keep their account so
  // anything they contributed stays attributed, and they sign in to nothing.
  async function removeStaff(person) {
    const r = await fetch(`/api/staff/${person.id}`, { method: "DELETE" });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      alert(d.error || "Couldn't remove them.");
      return;
    }
    load();
  }

  const load = () => {
    fetch("/api/agency")
      .then(r => { if (!r.ok) throw new Error("Failed to load"); return r.json(); })
      .then(setData)
      .catch(e => setError(e.message));
  };

  useEffect(() => { load(); }, []);

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  const agency = data?.agency;
  const stats  = data?.stats;
  const accentColor = agency?.brand_settings?.accent_color || "var(--color-accent)";

  return (
    <div style={styles.page}>
      <Topbar agencyName={agency?.name} user={user} onLogout={handleLogout} compact={compact} />
      <div style={{ ...styles.container, ...(compact ? styles.containerCompact : {}) }}>

        <div style={styles.headerRow}>
          <div>
            <p style={styles.label}>Agency</p>
            <h1 style={styles.heading}>{agency?.name || "Loading…"}</h1>
          </div>
          {/* Accent color swatch from brand settings */}
          {accentColor && (
            <div style={{ ...styles.colorSwatch, background: accentColor }}
              title={`Accent: ${accentColor}`} />
          )}
        </div>

        {error && <p style={{ color:"var(--color-critical)",fontSize:"var(--text-sm)",marginBottom:16 }}>{error}</p>}

        {/* Stats strip */}
        {stats && (
          <div style={styles.statsRow}>
            {[
              { label:"Clients",      value: stats.clients },
              { label:"Coordinators", value: stats.coordinators },
              { label:"Support staff", value: stats.caregivers },
              { label:"Contributors", value: stats.contributors ?? 0 },
            ].map(s => (
              <div key={s.label} style={styles.statCard}>
                <p style={styles.statValue}>{s.value}</p>
                <p style={styles.statLabel}>{s.label}</p>
              </div>
            ))}
            {agency && (
              <div style={{ ...styles.statCard, flex:2 }}>
                <StorageBar used={agency.storage_used} limit={agency.storage_limit} pct={agency.storage_pct} />
              </div>
            )}
          </div>
        )}

        <div style={{ ...styles.twoCol, ...(compact ? styles.oneCol : {}) }}>
          {/* Left: roster */}
          <div style={{ display:"flex",flexDirection:"column",gap:24 }}>
            <div>
              <div style={styles.sectionHeadingRow}>
                <h2 style={styles.sectionHeading}>Coordinators</h2>
                <button style={styles.addBtn} onClick={() => setShowStaff(true)}>+ Add</button>
              </div>
              <div style={styles.rosterList}>
                {(data?.coordinators||[]).length===0
                  ? <p style={styles.empty}>No coordinators yet.</p>
                  : (data?.coordinators||[]).map(p => <PersonRow key={p.id} person={p} onRemove={removeStaff} />)}
              </div>
            </div>
            <div>
              <div style={styles.sectionHeadingRow}>
                <h2 style={styles.sectionHeading}>Support staff</h2>
                <button style={styles.addBtn} onClick={() => setShowStaff(true)}>+ Add</button>
              </div>
              <div style={styles.rosterList}>
                {(data?.caregivers||[]).length===0
                  ? <p style={styles.empty}>No support staff yet.</p>
                  : (data?.caregivers||[]).map(p => <PersonRow key={p.id} person={p} onRemove={removeStaff} />)}
              </div>
            </div>
            {/* Contributors — family and friends invited to help with a client's
                profile. They are not staff: they hold view + contribute on the
                one client they were invited to and nothing else. Before this
                section existed they appeared in no bucket at all, so an admin
                could not see who had access to their clients. Invitations are
                sent by a coordinator from the client list, which is why there is
                no "+ Add" here. */}
            <div>
              <div style={styles.sectionHeadingRow}>
                <h2 style={styles.sectionHeading}>Contributors</h2>
              </div>
              <div style={styles.rosterList}>
                {(data?.contributors||[]).length===0
                  ? <p style={styles.empty}>
                      No contributors yet. A coordinator can invite a parent,
                      sibling or friend from any client's page.
                    </p>
                  : (data?.contributors||[]).map(p => <PersonRow key={p.id} person={p} onRemove={removeStaff} />)}
              </div>
            </div>

            {/* Former team members.
                COLLAPSED BY DEFAULT, and that is the design, not a default.
                At roughly 45% annual DSP turnover an eighty-person agency adds
                about thirty-five of these a year; rendered inline, the roster
                would be half ghosts inside three years and the live team would
                be the hard thing to find. Present when it's wanted, absent
                when it isn't.

                Hidden entirely when nobody has left, so a new agency is not
                shown an empty monument to staff it never had. */}
            {(data?.former || []).length > 0 && (
              <div>
                <button
                  style={styles.formerToggle}
                  onClick={() => setShowFormer(v => !v)}
                  aria-expanded={showFormer}
                >
                  <span>
                    {data.former.length} former team member
                    {data.former.length !== 1 ? "s" : ""}
                  </span>
                  <span style={styles.formerChevron}>{showFormer ? "⌃" : "⌄"}</span>
                </button>
                {showFormer && (
                  <div style={styles.rosterList}>
                    {data.former.map(p => (
                      <FormerRow key={p.id} person={p} onReinvite={startReinvite} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Branding — hidden until the logo and colour are actually rendered
                somewhere. See SHOW_BRANDING. */}
            {agency?.settings && <AccessSettingsPanel settings={agency.settings} onSaved={load} />}

            {SHOW_BRANDING && agency && <BrandingPanel agency={agency} onUpdated={load} />}
          </div>

          {/* Right: clients (first on a phone) */}
          <div style={compact ? { order: -1, minWidth: 0 } : { minWidth: 0 }}>
            <div style={styles.sectionHeadingRow}>
              <h2 style={styles.sectionHeading}>Clients</h2>
              <button style={styles.addBtn} onClick={() => setShowNewClient(true)}>+ Add</button>
            </div>
            <div style={styles.rosterList}>
              {(data?.clients||[]).length===0
                ? <p style={styles.empty}>No clients yet.</p>
                : (data?.clients||[]).map(c => (
                    <div
                      key={c.id}
                      style={{ ...pr.row, ...(compact ? pr.rowCompact : {}), cursor: "pointer" }}
                      onClick={() => navigate(`/coordinator/clients/${c.id}/edit`)}
                    >
                      <div style={{ ...pr.avatar,background:"var(--color-accent-subtle)",color:"var(--color-accent)",border:"none" }}>
                        {c.display_name[0]}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <ClientNameBlock
                          displayName={c.display_name}
                          fullName={c.full_name}
                          editable
                          onEdit={() => setEditTarget(c)}
                          nameStyle={pr.name}
                        />
                        {(c.age||c.location) && <p style={pr.email}>{[c.age,c.location].filter(Boolean).join(" · ")}</p>}
                      </div>
                      {/* Same panel the coordinator list opens. The endpoints
                          are capability-gated and the owner holds CAP_MANAGE. */}
                      <button type="button" style={{ ...pr.rowChip, ...(compact ? pr.rowChipCompact : {}) }}
                              onClick={e => { e.stopPropagation(); setAccessTarget(c); }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M19 8v6M22 11h-6"/></svg>
                        Invite someone who knows them
                      </button>
                    </div>
                  ))
              }
            </div>
          </div>
        </div>

      </div>

      {accessTarget && (
        <AccessPanel client={accessTarget} onClose={() => setAccessTarget(null)} />
      )}
      {editTarget && (
        <EditClientModal
          client={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => load()}
        />
      )}

      {showNewClient && (
        <NewClientModal
          onClose={() => setShowNewClient(false)}
          onCreate={id => { setShowNewClient(false); navigate(`/coordinator/clients/${id}/quick-start`); }}
        />
      )}

      {(showStaff || reinvite) && (
        <StaffModal
          agencyName={agency?.name}
          // The picker the restore suggestion ticks. This was never passed
          // before, so StaffModal's assign-at-hire block — which is gated on a
          // non-empty clients list — could not render from this page at all.
          // Nobody would have noticed: an absent optional section looks exactly
          // like a section that was never meant to be there.
          clients={data?.clients}
          prefill={reinvite}
          onClose={() => { setShowStaff(false); setReinvite(null); }}
          onChanged={load}
        />
      )}
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const tb = {
  bar:        { background:"var(--color-surface)",borderBottom:"1px solid var(--color-border)",padding:"12px 32px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12 },
  barCompact: { padding:"10px 16px" },
  left:       { display:"flex",alignItems:"center",gap:16 },
  wordmark:   { fontWeight:"var(--weight-medium)",fontSize:17,color:"var(--color-text-primary)",display:"flex",alignItems:"center",gap:6 },
  dot:        { display:"inline-block",width:8,height:8,borderRadius:"50%",background:"var(--color-accent)" },
  agencyName: { fontSize:"var(--text-sm)",color:"var(--color-text-tertiary)",borderLeft:"1px solid var(--color-border)",paddingLeft:16 },
  right:      { display:"flex",alignItems:"center",gap:16 },
  chip:       { display:"flex",alignItems:"center",gap:10,fontSize:"var(--text-sm)",color:"var(--color-text-secondary)" },
  avatar:     { width:30,height:30,borderRadius:"50%",background:"var(--color-accent-subtle)",color:"var(--color-accent)",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:"var(--weight-medium)",fontSize:13 },
  logoutBtn:  { fontSize:"var(--text-xs)",color:"var(--color-text-tertiary)",background:"none",border:"none",cursor:"pointer",padding:0 },
};

const pr = {
  rowChip: { font:"inherit",fontSize:"var(--text-sm)",height:32,padding:"0 12px",display:"inline-flex",alignItems:"center",gap:6,border:"1px solid var(--color-border-strong)",borderRadius:"var(--radius-md)",background:"var(--color-surface)",color:"var(--color-text-secondary)",cursor:"pointer",whiteSpace:"nowrap",flexShrink:0 },
  // Phone: the chip drops under the name instead of pushing the row wide.
  rowChipCompact: { flexBasis:"100%",height:"auto",minHeight:36,padding:"6px 12px",justifyContent:"center",whiteSpace:"normal",textAlign:"center" },
  row:    { display:"flex",alignItems:"center",gap:12,padding:"10px 0",borderBottom:"1px solid var(--color-border)",textDecoration:"none",color:"var(--color-text-primary)" },
  rowCompact: { flexWrap:"wrap",rowGap:8 },
  avatar: { width:36,height:36,borderRadius:"50%",background:"var(--color-bg-subtle)",border:"1px solid var(--color-border)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:"var(--weight-medium)",color:"var(--color-text-secondary)",flexShrink:0 },
  name:   { fontSize:"var(--text-sm)",fontWeight:"var(--weight-medium)",margin:0 },
  email:  { fontSize:"var(--text-xs)",color:"var(--color-text-tertiary)",margin:0 },
  relationship: { fontSize: "var(--text-xs)", color: "var(--color-accent)", background: "var(--color-accent-subtle)", border: "1px solid var(--color-accent)", borderRadius: 999, padding: "3px 10px", whiteSpace: "nowrap" },
  pendingTag: { fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-tertiary)", border: "1px solid var(--color-border)", borderRadius: 999, padding: "1px 6px", marginLeft: 8, fontWeight: "var(--weight-regular)" },
  removeBtn: { fontSize: "var(--text-xs)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "4px 10px", color: "var(--color-critical)", cursor: "pointer", font: "inherit", whiteSpace: "nowrap", marginLeft: 8 },
  count:  { fontSize:"var(--text-xs)",color:"var(--color-text-tertiary)",flexShrink:0 },
};

const brand = {
  wrap:           { display:"flex",flexDirection:"column",gap:16 },
  logoRow:        { display:"flex",alignItems:"center",gap:16,padding:"16px",background:"var(--color-bg-subtle)",border:"1px solid var(--color-border)",borderRadius:"var(--radius-lg)" },
  logoImg:        { maxHeight:60,maxWidth:240,objectFit:"contain" },
  logoPlaceholder:{ width:120,height:48,background:"var(--color-bg-inset)",border:"1px dashed var(--color-border)",borderRadius:"var(--radius-sm)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"var(--text-xs)",color:"var(--color-text-tertiary)" },
  uploadBtn:      { fontSize:"var(--text-sm)",color:"var(--color-text-secondary)",background:"var(--color-surface)",border:"1px solid var(--color-border)",borderRadius:"var(--radius-md)",padding:"var(--space-2) var(--space-3)",cursor:"pointer" },
  uploadHint:     { fontSize:11,color:"var(--color-text-tertiary)",margin:0 },
  colorRow:       { display:"flex",alignItems:"center",justifyContent:"space-between",gap:16,padding:"14px 16px",background:"var(--color-bg-subtle)",border:"1px solid var(--color-border)",borderRadius:"var(--radius-lg)" },
  colorLabel:     { fontSize:"var(--text-sm)",fontWeight:"var(--weight-medium)",color:"var(--color-text-primary)",margin:0 },
  colorHint:      { fontSize:"var(--text-xs)",color:"var(--color-text-tertiary)",marginTop:2 },
  colorInput:     { width:36,height:28,padding:2,border:"1px solid var(--color-border)",borderRadius:4,cursor:"pointer",background:"none" },
  saveColorBtn:   { fontSize:"var(--text-xs)",color:"var(--color-text-secondary)",background:"none",border:"1px solid var(--color-border)",borderRadius:"var(--radius-sm)",padding:"3px var(--space-3)",cursor:"pointer" },
};

const styles = {
  page:           { minHeight:"100dvh",background:"var(--color-bg-subtle)" },
  container:      { maxWidth:1000,margin:"0 auto",padding:"40px 32px 80px",overflowX:"hidden" },
  containerCompact: { padding:"20px 16px 60px" },
  headerRow:      { display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:28 },
  label:          { fontSize:"var(--text-xs)",fontWeight:"var(--weight-semibold)",textTransform:"uppercase",letterSpacing:"0.08em",color:"var(--color-text-tertiary)",marginBottom:4 },
  heading:        { fontSize:"var(--text-2xl)",fontWeight:"var(--weight-medium)",color:"var(--color-text-primary)" },
  colorSwatch:    { width:24,height:24,borderRadius:"var(--radius-full)",flexShrink:0,border:"1px solid var(--color-border)" },
  statsRow:       { display:"flex",gap:16,marginBottom:32,flexWrap:"wrap" },
  statCard:       { flex:1,minWidth:100,background:"var(--color-surface)",border:"1px solid var(--color-border)",borderRadius:"var(--radius-lg)",padding:"18px 20px" },
  statValue:      { fontSize:28,fontWeight:"var(--weight-semibold)",color:"var(--color-text-primary)",lineHeight:1 },
  statLabel:      { fontSize:"var(--text-xs)",color:"var(--color-text-tertiary)",marginTop:4 },
  twoCol:         { display:"grid",gridTemplateColumns:"1fr 1fr",gap:32,minWidth:0 },
  // Phone: one column, clients first — they are what the owner comes for.
  oneCol:         { gridTemplateColumns:"minmax(0, 1fr)",gap:24 },
  sectionHeadingRow: { display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 },
  addBtn:         { fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "2px var(--space-3)", cursor: "pointer" },
  // Still a placeholder on the Clients section — a coordinator creates clients
  // from their own list, which is a different question from adding staff.
  addBtnOff:      { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "2px var(--space-3)", cursor: "not-allowed", opacity: 0.6 },
  sectionHeading: { fontSize:"var(--text-sm)",fontWeight:"var(--weight-semibold)",color:"var(--color-text-secondary)",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8 },
  rosterList:     { background:"var(--color-surface)",border:"1px solid var(--color-border)",borderRadius:"var(--radius-lg)",padding:"0 16px" },
  // Reads as a disclosure, not a section heading: lighter than the uppercase
  // headings above it, because the live team is what this page is for and the
  // departed are a drawer you open.
  formerToggle:   { font:"inherit",width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,background:"none",border:"none",padding:"10px 0",marginTop:4,fontSize:"var(--text-sm)",color:"var(--color-text-tertiary)",cursor:"pointer",textAlign:"left" },
  formerChevron:  { fontSize:11,color:"var(--color-text-tertiary)" },
  empty:          { fontSize:"var(--text-sm)",color:"var(--color-text-tertiary)",padding:"12px 0" },
};
